import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { Icon } from '@livable/saas-react-components';
import { Box, Button, ButtonBase, Checkbox, FormControlLabel, Popover, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import useStyles from './billsFilterPanelStyles';
import { UNKNOWN_PROPERTY, categoryOptions, changedCategoryCount, describeServicePeriod, pickedValues, visibleFilterCategories, withCategoryCleared, withOptionToggled } from './billsFilterCategories';

/**
 * What the category list says beside a category in use: how many are picked, the one value of a single choice, or a
 * date range's days.
 */
function summaryOf(category, filters, options) {
  var _categoryOptions$find, _categoryOptions$find2;
  const picked = pickedValues(filters, category.key);
  if (!picked.length) return '';
  if (category.kind === 'multi') return String(picked.length);
  if (category.kind === 'dateRange') {
    return describeServicePeriod(filters[category.fromKey], filters[category.toKey], {
      short: true
    });
  }
  return (_categoryOptions$find = (_categoryOptions$find2 = categoryOptions(category.key, options).find(option => option.value === picked[0])) === null || _categoryOptions$find2 === void 0 ? void 0 : _categoryOptions$find2.label) !== null && _categoryOptions$find !== void 0 ? _categoryOptions$find : '';
}

/** A pane's title, and Clear while its filter is on (named for it, since every pane has one) or a hint while it is not. */
function PaneHeader(_ref) {
  let category = _ref.category,
    hint = _ref.hint,
    isOn = _ref.isOn,
    onClear = _ref.onClear;
  const classes = useStyles();
  return /*#__PURE__*/React.createElement(Box, {
    className: classes.optionsHeader
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.optionsTitle
  }, category.label), isOn ? /*#__PURE__*/React.createElement(Button, {
    variant: "text",
    size: "small",
    className: classes.clearButton,
    "aria-label": "Clear ".concat(category.label),
    onClick: onClear
  }, "Clear") : /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.optionsHint
  }, hint));
}
const DAY = moment.HTML5_FMT.DATE;
const dateOf = day => day ? moment(day, DAY, true).toDate() : null;
const dayOf = date => date ? moment(date).format(DAY) : '';

/**
 * Service period as a range of days (LB-6087), on a calendar in the pane rather than in a popup over it, which the panel
 * would clip. The first click is the first day and the second the last, and both go into the panel's draft as they are
 * picked: a first day alone is an open range ("from then on"), so Apply never quietly drops half a pick. The list shows
 * the bills whose service period overlaps the range. The month and year can be jumped to rather than paged through.
 */
function ServicePeriodPane(_ref2) {
  let category = _ref2.category,
    filters = _ref2.filters,
    onChange = _ref2.onChange;
  const classes = useStyles();
  const from = filters[category.fromKey];
  const to = filters[category.toKey];
  const startDate = dateOf(from);
  // Never an end without a start: the calendar takes no clicks at all in that state, as a link naming only the last
  // day would leave it. Such a link is still shown, in words, and the first click starts a range of its own.
  const endDate = from ? dateOf(to) : null;
  const handleRangeChange = _ref3 => {
    let _ref4 = _slicedToArray(_ref3, 2),
      start = _ref4[0],
      end = _ref4[1];
    return onChange({
      [category.fromKey]: dayOf(start),
      [category.toKey]: dayOf(end)
    });
  };
  const readout = from && !to ? "From ".concat(moment(from, DAY).format('MMM D, YYYY'), ". Pick the last day, or leave it open.") : describeServicePeriod(from, to) || 'Pick the first day, then the last.';
  return /*#__PURE__*/React.createElement(Box, {
    id: "bills-filter-options",
    className: classes.optionsPane
  }, /*#__PURE__*/React.createElement(PaneHeader, {
    category: category,
    hint: "Pick a range",
    isOn: Boolean(from || to),
    onClear: () => onChange(withCategoryCleared(filters, category.key))
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    role: "status",
    className: classes.periodReadout
  }, readout), /*#__PURE__*/React.createElement(Box, {
    className: classes.periodCalendar
  }, /*#__PURE__*/React.createElement(DatePicker, {
    inline: true,
    selectsRange: true,
    selected: startDate,
    startDate: startDate,
    endDate: endDate,
    onChange: handleRangeChange,
    showMonthDropdown: true,
    showYearDropdown: true,
    dropdownMode: "select",
    minDate: new Date(2000, 0, 1),
    maxDate: new Date(2099, 11, 31)
  })), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.optionsHint
  }, "Lists the bills whose service period overlaps these days."));
}

/** The service provider list before it has anything to show: on its way, failed, or truly empty. Never "no options". */
function renderProvidersState(classes, providers) {
  if (providers.isLoading) {
    return /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      role: "status",
      className: classes.optionsMessage
    }, "Loading service providers\u2026");
  }
  if (providers.isFailed) {
    return /*#__PURE__*/React.createElement(Box, {
      className: "".concat(classes.optionsMessage, " ").concat(classes.optionsFailure),
      role: "alert"
    }, /*#__PURE__*/React.createElement("span", null, "The service providers could not be loaded."), /*#__PURE__*/React.createElement(Button, {
      size: "small",
      variant: "outlined",
      startIcon: /*#__PURE__*/React.createElement(Icon, {
        type: "reload"
      }),
      onClick: providers.onRetry
    }, "Retry"));
  }
  return /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.optionsMessage
  }, "No service providers yet.");
}
function OptionsPane(_ref5) {
  var _category$emptyText, _picked$;
  let category = _ref5.category,
    filters = _ref5.filters,
    onChange = _ref5.onChange,
    options = _ref5.options,
    providers = _ref5.providers;
  const classes = useStyles();
  const _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    search = _useState2[0],
    setSearch = _useState2[1];
  const offered = categoryOptions(category.key, options);
  const picked = pickedValues(filters, category.key);
  const isProviders = category.key === 'providers';

  /**
   * The service provider list is read once, when the page opens, and quietly (LB-6085). Opened while it is still empty
   * and not on its way, it is read again rather than left saying there is nothing to pick -- once per opening: this pane
   * mounts each time the list is shown, and asking again whenever a read came back empty would never stop. After that
   * a failed read waits for Retry.
   */
  useEffect(() => {
    if (isProviders && offered.length === 0 && !providers.isLoading) providers.onRetry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Picked but not offered: a provider or property a link named that the lists do not have. Shown first, ticked, so
  // it can still be seen and unpicked here as well as from its chip.
  const unlisted = picked.filter(value => !offered.some(option => option.value === value)).map(value => ({
    value,
    label: category.key === 'properties' ? UNKNOWN_PROPERTY : value
  }));
  const term = search.trim().toLowerCase();
  const shown = [...unlisted, ...offered].filter(option => !term || option.label.toLowerCase().includes(term));
  const handleOptionToggle = value => onChange(withOptionToggled(filters, category, value, offered));
  return /*#__PURE__*/React.createElement(Box, {
    id: "bills-filter-options",
    className: classes.optionsPane
  }, /*#__PURE__*/React.createElement(PaneHeader, {
    category: category,
    hint: category.kind === 'single' ? 'Pick one' : 'Pick any',
    isOn: picked.length > 0,
    onClear: () => onChange(withCategoryCleared(filters, category.key))
  }), category.searchLabel && offered.length > 0 ? /*#__PURE__*/React.createElement(TextField, {
    className: classes.search,
    size: "small",
    variant: "outlined",
    placeholder: category.searchLabel,
    value: search,
    onChange: event => setSearch(event.target.value),
    inputProps: {
      'aria-label': category.searchLabel
    }
  }) : null, isProviders && offered.length === 0 && unlisted.length === 0 ? renderProvidersState(classes, providers) : shown.length === 0 ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.optionsMessage
  }, term ? 'Nothing matches that.' : (_category$emptyText = category.emptyText) !== null && _category$emptyText !== void 0 ? _category$emptyText : 'Nothing to pick yet.') : category.kind === 'single' ? /*#__PURE__*/React.createElement(RadioGroup, {
    "aria-label": category.label,
    className: classes.optionList,
    value: (_picked$ = picked[0]) !== null && _picked$ !== void 0 ? _picked$ : '',
    onChange: event => handleOptionToggle(event.target.value)
  }, shown.map(option => /*#__PURE__*/React.createElement(FormControlLabel, {
    key: option.value,
    value: option.value,
    control: /*#__PURE__*/React.createElement(Radio, {
      size: "small"
    }),
    label: option.label,
    title: option.label
  }))) : /*#__PURE__*/React.createElement(Box, {
    role: "group",
    "aria-label": category.label,
    className: classes.optionList
  }, shown.map(option => /*#__PURE__*/React.createElement(FormControlLabel, {
    key: option.value,
    control: /*#__PURE__*/React.createElement(Checkbox, {
      size: "small",
      checked: picked.includes(option.value),
      onChange: () => handleOptionToggle(option.value)
    }),
    label: option.label,
    title: option.label
  }))), isProviders && providers.isTruncated ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    className: classes.optionsHint
  }, "The first 500 service providers are listed.") : null);
}

/**
 * What the panel shows while it is open: the categories, the shown one's options, and the draft being picked.
 *
 * The draft and the shown category live here rather than on the page, so a tick re-renders the panel and not the
 * Bills table under it, up to a hundred rows of seventeen columns. It mounts afresh each time the panel opens (the
 * page keys it per opening), so every opening starts from the filters the list shows, and it stays through the
 * closing fade, so a closing panel does not flick back to them.
 */
function PanelContent(_ref6) {
  var _categories$find;
  let appliedFilters = _ref6.appliedFilters,
    initialCategoryKey = _ref6.initialCategoryKey,
    onApply = _ref6.onApply,
    onClose = _ref6.onClose,
    options = _ref6.options,
    providers = _ref6.providers,
    showApPush = _ref6.showApPush;
  const classes = useStyles();
  const _useState3 = useState(appliedFilters),
    _useState4 = _slicedToArray(_useState3, 2),
    draft = _useState4[0],
    setDraft = _useState4[1];
  const _useState5 = useState(initialCategoryKey),
    _useState6 = _slicedToArray(_useState5, 2),
    categoryKey = _useState6[0],
    setCategoryKey = _useState6[1];
  const categories = visibleFilterCategories({
    showApPush
  });
  const category = (_categories$find = categories.find(candidate => candidate.key === categoryKey)) !== null && _categories$find !== void 0 ? _categories$find : categories[0];
  const changed = changedCategoryCount(draft, appliedFilters, {
    showApPush
  });
  const handleDraftChange = next => setDraft(current => _objectSpread(_objectSpread({}, current), next));
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    className: classes.panelBody
  }, /*#__PURE__*/React.createElement(Box, {
    role: "group",
    "aria-label": "Filter by",
    className: classes.categories
  }, categories.map(candidate => {
    const isCurrent = candidate.key === category.key;
    return /*#__PURE__*/React.createElement(ButtonBase, {
      key: candidate.key,
      "aria-current": isCurrent ? 'true' : undefined,
      "aria-controls": "bills-filter-options",
      className: "".concat(classes.category, " ").concat(isCurrent ? classes.categoryCurrent : ''),
      onClick: () => setCategoryKey(candidate.key)
    }, /*#__PURE__*/React.createElement("span", null, candidate.label), /*#__PURE__*/React.createElement("span", {
      className: classes.categorySummary
    }, summaryOf(candidate, draft, options)));
  })), category.kind === 'dateRange' ? /*#__PURE__*/React.createElement(ServicePeriodPane, {
    key: category.key,
    category: category,
    filters: draft,
    onChange: handleDraftChange
  }) : /*#__PURE__*/React.createElement(OptionsPane, {
    key: category.key,
    category: category,
    filters: draft,
    onChange: handleDraftChange,
    options: options,
    providers: providers
  })), /*#__PURE__*/React.createElement(Box, {
    className: classes.panelFooter
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    role: "status",
    className: classes.optionsHint
  }, changed === 0 ? 'Pick filters, then Apply.' : "".concat(changed, " ").concat(changed === 1 ? 'filter' : 'filters', " changed")), /*#__PURE__*/React.createElement(Box, {
    className: classes.panelActions
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "text",
    size: "small",
    className: classes.cancelButton,
    onClick: onClose
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    size: "small",
    onClick: () => onApply(draft),
    disabled: changed === 0
  }, "Apply"))));
}

/**
 * The one place the Bills page is filtered from (LB-6087).
 *
 * It replaces four: the three toolbar dropdowns, the Filters popover, and the Allocation and AP Push header menus. The
 * categories are listed on the left and the picked one's options on the right, so nothing opens inside anything else
 * and a long list scrolls in its own pane instead of running off the screen. The "+ Filter" button opens it at the
 * first category; a column's funnel opens it at that column's.
 *
 * Picks are a draft until Apply, so several filters are one read of the list rather than one each; Cancel, Escape or a
 * click outside drops them. `filters` is what the list shows. `openId` changes with each opening, which starts a fresh
 * draft from it.
 */
export function BillsFilterPanel(_ref7) {
  let anchorEl = _ref7.anchorEl,
    categoryKey = _ref7.categoryKey,
    filters = _ref7.filters,
    onApply = _ref7.onApply,
    onClose = _ref7.onClose,
    openId = _ref7.openId,
    options = _ref7.options,
    providers = _ref7.providers,
    showApPush = _ref7.showApPush;
  const classes = useStyles();
  return /*#__PURE__*/React.createElement(Popover, {
    id: "bills-filter-panel",
    anchorEl: anchorEl,
    open: Boolean(anchorEl),
    onClose: onClose,
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left'
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left'
    },
    PaperProps: {
      className: classes.panelPaper,
      role: 'dialog',
      'aria-label': 'Filters'
    }
  }, /*#__PURE__*/React.createElement(PanelContent, {
    key: openId,
    appliedFilters: filters,
    initialCategoryKey: categoryKey,
    onApply: onApply,
    onClose: onClose,
    options: options,
    providers: providers,
    showApPush: showApPush
  }));
}